import { Component, OnInit, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';
import { AffluenceThresholdsService, AffluenceThresholds } from '../../core/services/affluence-thresholds.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface QualityThresholds{satisfactionMinPercent:number;qrParticipationMinPercent:number;qrScanMinCount:number|null;constrainedDaysMaxPercent:number;contactRequestsMaxCount:number;responseRateMinPercent:number;reliabilityMinPercent:number;allowOutOfHoursComments:boolean;responseDelayMaxMinutes:number|null;actionCount:number|null;perceivedWaitThreshold?:string}
interface DocumentValidator{civility:string|null;fullName:string;roleLabel:string;signOrder:number;mandatory:boolean}
interface AllThresholds{quality:QualityThresholds;documentValidators?:DocumentValidator[]}

@Component({
  selector: 'app-objectifs',
  standalone: true,
  imports: [CommonModule, FormsModule, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './objectifs.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./objectifs.component.scss']
})
export class ObjectifsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly toastService = inject(ToastService);
  private readonly baseRoot = inject(API_BASE_URL).replace(/\/+$/, '');
  private readonly affluenceSvc = inject(AffluenceThresholdsService);
  private readonly translate = inject(TranslateService);

  allThresholds: AllThresholds | null = null;
  loading = false;
  saving = false;

  // ---- Seuils d'affluence (Fluide / Modéré / Forte affluence) ----
  orgDefaultThresholds: AffluenceThresholds | null = null;
  estOverrideThresholds: AffluenceThresholds | null = null;
  hasEstOverride = false;
  customizeForSite = false;
  loadingAffluence = false;
  savingOrgDefault = false;
  savingEstOverride = false;
  clearingEstOverride = false;

  get currentEstablishmentName(): string {
    const id = this.session.selectedId();
    return this.session.establishments().find(e => e.id === id)?.name || this.translate.instant('pilotage.objectifs.thisSite');
  }

  addValidator(){if(!this.allThresholds)return;if(!this.allThresholds.documentValidators)this.allThresholds.documentValidators=[];this.allThresholds.documentValidators.push({civility:'M.',fullName:'',roleLabel:'',signOrder:(this.allThresholds.documentValidators.length+1),mandatory:true})}
  removeValidator(i:number){if(this.allThresholds?.documentValidators)this.allThresholds.documentValidators.splice(i,1)}
  trackValidator(i:number,v:DocumentValidator):number{return i}

  constructor() {
    effect(() => {
      const est = this.session.selectedId();
      if (est) {
        this.loadThresholds(est);
        this.loadAffluenceThresholds(est);
      }
    });
  }

  ngOnInit(): void {}

  private loadThresholds(id:string):void{
    this.loading=true;
    this.http.get<any>(`${this.baseRoot}/api/v1/quality/thresholds/${encodeURIComponent(id)}`).subscribe({
      next:d=>{this.allThresholds=d;if(this.allThresholds&&!this.allThresholds.documentValidators)this.allThresholds.documentValidators=[];this.loading=false},
      error:e=>{console.error('Erreur:',e);this.loading=false}
    })
  }

  saveThresholds():void{
    const e=this.session.selectedId();
    if(!e||!this.allThresholds||this.saving)return;
    this.saving=true;
    
    this.http.put(`${this.baseRoot}/api/v1/quality/thresholds/${encodeURIComponent(e)}`,{quality:this.allThresholds.quality}).subscribe({
      next:()=>{
        this.saveValidators(e);
      },
      error:r=>{console.error('Erreur:',r);this.saving=false}
    })
  }

  private saveValidators(establishmentId: string): void {
    if (!this.allThresholds?.documentValidators) {
      this.saving = false;
      this.toastService.show(this.translate.instant('pilotage.objectifs.toast.thresholdsSavedTitle'),'success',3000,this.translate.instant('pilotage.objectifs.toast.thresholdsSavedBody'));
      return;
    }

    const validators = this.allThresholds.documentValidators
      .filter(v => v.fullName.trim() && v.roleLabel.trim())
      .map((v, index) => ({
        civility: v.civility || 'M.',
        fullName: v.fullName.trim(),
        roleLabel: v.roleLabel.trim(),
        signOrder: index + 1,
        isActive: true
      }));

    this.http.put(`${this.baseRoot}/api/v1/quality/thresholds/${encodeURIComponent(establishmentId)}/validators`, { validators }).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.thresholdsSavedTitle'),'success',3000,this.translate.instant('pilotage.objectifs.toast.thresholdsSavedBody'));
      },
      error: r => {
        console.error('Erreur validateurs:', r);
        this.saving = false;
      }
    });
  }

  private loadAffluenceThresholds(establishmentId: string): void {
    this.loadingAffluence = true;
    this.affluenceSvc.getOrgDefault().subscribe({
      next: d => { this.orgDefaultThresholds = d; },
      error: e => console.error('Erreur chargement seuils par défaut:', e),
    });
    this.affluenceSvc.getForEstablishment(establishmentId).subscribe({
      next: d => {
        this.hasEstOverride = d.isOverride;
        this.customizeForSite = d.isOverride;
        this.estOverrideThresholds = { ...d.thresholds };
        this.loadingAffluence = false;
      },
      error: e => {
        console.error('Erreur chargement seuils du site:', e);
        this.loadingAffluence = false;
      },
    });
  }

  saveOrgDefaultThresholds(): void {
    if (!this.orgDefaultThresholds || this.savingOrgDefault) return;
    this.savingOrgDefault = true;
    this.affluenceSvc.setOrgDefault(this.orgDefaultThresholds).subscribe({
      next: () => {
        this.savingOrgDefault = false;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.orgDefaultSavedTitle'), 'success', 3000, this.translate.instant('pilotage.objectifs.toast.orgDefaultSavedBody'));
      },
      error: e => {
        console.error('Erreur enregistrement seuils par défaut:', e);
        this.savingOrgDefault = false;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.errorTitle'), 'error', 4000, this.translate.instant('pilotage.objectifs.toast.orgDefaultSaveError'));
      },
    });
  }

  startCustomizeForSite(): void {
    this.customizeForSite = true;
  }

  cancelCustomizeForSite(): void {
    this.customizeForSite = this.hasEstOverride;
    const est = this.session.selectedId();
    if (est) this.loadAffluenceThresholds(est);
  }

  saveEstOverrideThresholds(): void {
    const id = this.session.selectedId();
    if (!id || !this.estOverrideThresholds || this.savingEstOverride) return;
    this.savingEstOverride = true;
    this.affluenceSvc.setForEstablishment(id, this.estOverrideThresholds).subscribe({
      next: () => {
        this.savingEstOverride = false;
        this.hasEstOverride = true;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.overrideSavedTitle'), 'success', 3000, this.translate.instant('pilotage.objectifs.toast.overrideSavedBody', { site: this.currentEstablishmentName }));
      },
      error: e => {
        console.error('Erreur enregistrement surcharge:', e);
        this.savingEstOverride = false;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.errorTitle'), 'error', 4000, this.translate.instant('pilotage.objectifs.toast.overrideSaveError'));
      },
    });
  }

  clearEstOverrideThresholds(): void {
    const id = this.session.selectedId();
    if (!id || this.clearingEstOverride) return;
    this.clearingEstOverride = true;
    this.affluenceSvc.clearForEstablishment(id).subscribe({
      next: () => {
        this.clearingEstOverride = false;
        this.hasEstOverride = false;
        this.customizeForSite = false;
        this.loadAffluenceThresholds(id);
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.resetToDefaultTitle'), 'success', 3000, this.translate.instant('pilotage.objectifs.toast.resetToDefaultBody', { site: this.currentEstablishmentName }));
      },
      error: e => {
        console.error('Erreur suppression surcharge:', e);
        this.clearingEstOverride = false;
        this.toastService.show(this.translate.instant('pilotage.objectifs.toast.errorTitle'), 'error', 4000, this.translate.instant('pilotage.objectifs.toast.resetError'));
      },
    });
  }

  private draggedIndex: number | null = null;

  onDragStart(event: DragEvent, index: number): void {
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', '');
    }
    (event.target as HTMLElement).style.opacity = '0.5';
  }

  onDragEnd(event: DragEvent): void {
    (event.target as HTMLElement).style.opacity = '1';
    this.draggedIndex = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).style.backgroundColor = 'rgba(111, 66, 193, 0.1)';
  }

  onDragLeave(event: DragEvent): void {
    (event.currentTarget as HTMLElement).style.backgroundColor = '';
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).style.backgroundColor = '';
    
    if (this.draggedIndex !== null && this.draggedIndex !== dropIndex && this.allThresholds?.documentValidators) {
      const validators = this.allThresholds.documentValidators;
      const draggedItem = validators[this.draggedIndex];
      validators.splice(this.draggedIndex, 1);
      validators.splice(dropIndex, 0, draggedItem);
    }
    this.draggedIndex = null;
  }

  getFormattedDelay(minutes: number): string {
    if (minutes < 60) {
      return this.translate.instant('pilotage.objectifs.delay.minutes', { minutes });
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0
        ? this.translate.instant('pilotage.objectifs.delay.hoursMinutes', { hours, minutes: remainingMinutes })
        : this.translate.instant('pilotage.objectifs.delay.hours', { hours });
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      return remainingHours > 0
        ? this.translate.instant('pilotage.objectifs.delay.daysHours', { days, hours: remainingHours })
        : this.translate.instant('pilotage.objectifs.delay.days', { days });
    }
  }


}
